import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import FormHelperText from '@material-ui/core/FormHelperText'
import ServerAutocomplete from './Autocomplete'
import useApi from '../hooks/useApi'
import {Alert} from '@material-ui/lab'
import {ITALIA, nationalities} from "../main/specificUtils/configs";

const useStyles = makeStyles(theme => ({
    autocompleteContainer: {
        marginTop: theme.spacing(3),
    },
}))

function ShippingAutocomplete(props) {
    const classes = useStyles()
    const {getComuniByName} = useApi()
    const [availableCaps, setAvailableCaps] = useState([])
    const [selectedCity, setSelectedCity] = useState(null)

    const {
        styles,
        replaceClasses = {
            street: '',
            city: '',
            zipCode: '',
            province: '',
            region: '',
            nation: '',
            additionalInfo: '',
        },
        asResidence,
        onAsResidenceChange,
        streetAddress,
        streetAddressError,
        onStreetAddressChange,
        city,
        cityError,
        onCityChange,
        cap,
        capError,
        onCapChange,
        province,
        provinceError,
        onProvinceChange,
        region,
        regionError,
        onRegionChange,
        nation,
        nationError,
        onNationChange,
        additionalInfo,
        onAdditionalInfoChange,
    } = props

    const [isItalian, setIsItalian] = useState(true)

    useEffect(() => {
        const current = (nation || '').toLowerCase()
        if (current === ITALIA.toLowerCase()) {
            if (!isItalian) setIsItalian(true)
        } else if (current !== '') {
            if (isItalian) setIsItalian(false)
        }
    }, [nation])

    const nazionalitaItaliana = nationalities[0].value;
    const nazionalitaEstera = nationalities[1].value;
    
    const handleNationalityChange = (e) => {
        const value = e.target.value
        if (value === nazionalitaItaliana) {
            setIsItalian(true)
            onNationChange && onNationChange(ITALIA)
        } else {
            setIsItalian(false)
            if ((nation || '').toLowerCase() === ITALIA.toLowerCase()) {
                onNationChange && onNationChange('')
            }
        }
    }

    const onCitySelect = (val) => {
        const selected = val || null
        const newCity = selected ? selected.name : ''
        const newCapList = selected && selected.cap ? selected.cap.split(',') : []
        const preferredCap = newCapList && newCapList.length > 0 ? newCapList[0] : ''
        onCityChange && onCityChange(newCity)
        if (preferredCap) {
            onCapChange && onCapChange(preferredCap)
        }
        if (selected) {
            onProvinceChange && onProvinceChange(selected.car || '')
            onRegionChange && onRegionChange(selected.region || '')
        }
        setAvailableCaps(newCapList)
        setSelectedCity(selected)
    }

    useEffect(() => {
        if (city && (!selectedCity || selectedCity.name !== city)) {
            setSelectedCity({name: city, label: city})
        }
        if (!city && selectedCity) {
            setSelectedCity(null)
        }
    }, [city])

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">
                        La residenza inserita è la stessa per l'invio del kit necessario al ragazzo per la partenza?
                    </FormLabel>
                    <RadioGroup
                        row
                        name="shippingAsResidence"
                        value={asResidence === null ? '' : asResidence ? '1' : '0'}
                        onChange={(e) => onAsResidenceChange && onAsResidenceChange(e.target.value === '1')}
                    >
                        <FormControlLabel value="1" control={<Radio/>} label="Sì"/>
                        <FormControlLabel value="0" control={<Radio/>} label="No"/>
                    </RadioGroup>
                </FormControl>
                {(asResidence === null) && (streetAddressError) && (<FormHelperText className={styles && styles.errorMessage}>
                    Selezionare un'opzione per procedere
                </FormHelperText>)}
            </Grid>

            {asResidence === false ? (
                <>
                    <Grid item xs={12}>
                        <Alert severity="warning">
                            Attenzione: verifica accuratamente i dati inseriti per evitare disguidi nella spedizione del kit.<br/>Per indirizzi in nazioni estere, qualora alcuni dei campi sottostanti non siano applicabili, indicare comunque un trattino (-)
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Nazionalità indirizzo di spedizione *</FormLabel>
                            <TextField
                                select
                                value={isItalian ? nazionalitaItaliana : nazionalitaEstera}
                                onChange={handleNationalityChange}
                                SelectProps={{native: true}}
                                margin="normal"
                            >
                                {nationalities.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.value}
                                    </option>
                                ))}
                            </TextField>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Via/Piazza *"
                            className={replaceClasses.street}
                            value={streetAddress}
                            error={!!streetAddressError}
                            helperText={streetAddressError}
                            onChange={onStreetAddressChange}
                            margin="normal"
                            fullWidth
                        />
                    </Grid>

                    {isItalian ? (
                        <>
                            <Grid item xs={12} md={3} className={classes.autocompleteContainer}>
                                <ServerAutocomplete
                                    value={selectedCity}
                                    className={replaceClasses.city}
                                    placeholder="Città *"
                                    onDelete={() => onCitySelect(null)}
                                    onSelect={onCitySelect}
                                    getOptions={getComuniByName}
                                />
                                {!!cityError && (
                                    <FormHelperText className={styles && styles.errorMessage}>
                                        {cityError}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="CAP *"
                                    className={replaceClasses.zipCode}
                                    value={cap}
                                    error={!!capError}
                                    helperText={
                                        capError || (availableCaps.length > 1 && 'CAP multipli per la città selezionata, verificare che il dato attuale sia corretto.')
                                    }
                                    onChange={onCapChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Provincia *"
                                    className={replaceClasses.province}
                                    value={province}
                                    error={!!provinceError}
                                    helperText={provinceError}
                                    onChange={onProvinceChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Regione *"
                                    className={replaceClasses.region}
                                    value={region}
                                    error={!!regionError}
                                    helperText={regionError}
                                    onChange={onRegionChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Nazione *"
                                    className={replaceClasses.nation}
                                    value={nation || ITALIA}
                                    onChange={onNationChange}
                                    margin="normal"
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Città *"
                                    className={replaceClasses.city}
                                    value={city}
                                    error={!!cityError}
                                    helperText={cityError}
                                    onChange={onCityChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="CAP *"
                                    className={replaceClasses.zipCode}
                                    value={cap}
                                    error={!!capError}
                                    helperText={capError}
                                    onChange={onCapChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Provincia *"
                                    className={replaceClasses.province}
                                    value={province}
                                    error={!!provinceError}
                                    helperText={provinceError}
                                    onChange={onProvinceChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Regione *"
                                    className={replaceClasses.region}
                                    value={region}
                                    error={!!regionError}
                                    helperText={regionError}
                                    onChange={onRegionChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Nazione *"
                                    className={replaceClasses.nation}
                                    value={nation}
                                    error={!!nationError}
                                    helperText={nationError}
                                    onChange={onNationChange}
                                    margin="normal"
                                    fullWidth
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            label="Informazioni utili alla spedizione"
                            value={additionalInfo}
                            onChange={onAdditionalInfoChange}
                            className={replaceClasses.additionalInfo}
                            margin="normal"
                            fullWidth
                            placeholder="Es. c/o Famiglia Rossi, portineria, suonare citofono 3° piano..."
                            helperText="Informazioni utili per facilitare la consegna (opzionale)"
                        />
                    </Grid>
                </>
            ) : asResidence === true ? (
                <>
                    <Grid item xs={12}>
                        <TextField
                            label="Informazioni utili alla spedizione"
                            value={additionalInfo}
                            onChange={onAdditionalInfoChange}
                            className={replaceClasses.additionalInfo}
                            margin="normal"
                            fullWidth
                            placeholder="Es. c/o Famiglia Rossi, portineria, suonare citofono 3° piano..."
                            helperText="Informazioni utili per facilitare la consegna (opzionale)"
                        />
                    </Grid>
                </>
            ) : (
                <></>
            )}
        </Grid>
    )
}

ShippingAutocomplete.propTypes = {
    styles: PropTypes.object,
    replaceClasses: PropTypes.object,
    asResidence: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
    onAsResidenceChange: PropTypes.func.isRequired,
    streetAddress: PropTypes.string,
    streetAddressError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onStreetAddressChange: PropTypes.func.isRequired,
    city: PropTypes.string,
    cityError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onCityChange: PropTypes.func.isRequired,
    cap: PropTypes.string,
    capError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onCapChange: PropTypes.func.isRequired,
    province: PropTypes.string,
    provinceError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onProvinceChange: PropTypes.func.isRequired,
    region: PropTypes.string,
    regionError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onRegionChange: PropTypes.func.isRequired,
    nation: PropTypes.string,
    nationError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onNationChange: PropTypes.func.isRequired,
    additionalInfo: PropTypes.string,
    onAdditionalInfoChange: PropTypes.func.isRequired,
}

export default ShippingAutocomplete
